import { Badge, Button, Flex, Stack, Text, Box, Loader, Paper } from '@mantine/core';
import { IconCar, IconInfoHexagon } from '@tabler/icons-react';
import useLang from 'lang';
import sprintf from 'lang/sprintf';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

/** Direct URL to the garage controller on the local network. */
const GARAGE_CONTROLLER_URL = 'http://192.168.88.10:8080/garage.cgi';

/** Re-poll interval while user is in queue (ms). */
const POLL_INTERVAL_MS = 5_000;

/**
 * Phase of the garage UI state machine.
 */
type GaragePhase = 'empty' | 'loading' | 'waiting' | 'parked' | 'error';

/**
 * Accepted values returned by the controller in REQ_ACCEPTED.
 */
type ReqAccepted = 'YES' | 'NOT' | 'IN_PROCESS' | 'QUEUE_FULL';

/**
 * Error cause stored when phase === 'error'.
 */
type GarageError = 'NOT' | 'QUEUE_FULL' | 'NETWORK';

/**
 * Parsed response from the garage controller.
 */
interface ControllerResponse {
  reqAccepted: ReqAccepted;
  queuePosition: number;
}

/**
 * Parse the plain-text KEY=VALUE response body.
 */
function parseControllerResponse(text: string): ControllerResponse {
  const map: Record<string, string> = {};
  text
    .trim()
    .split('\n')
    .forEach((line) => {
      const eqIdx = line.indexOf('=');
      if (eqIdx !== -1) map[line.slice(0, eqIdx).trim()] = line.slice(eqIdx + 1).trim();
    });
  return {
    reqAccepted: (map['REQ_ACCEPTED'] ?? 'NOT') as ReqAccepted,
    queuePosition: parseInt(map['QUEUE_POSITION'] ?? '0', 10),
  };
}

/**
 * Call the garage controller for the given user number (1–19).
 * The full ID sent is 2xx, e.g. user 1 → ID=201.
 */
async function callController(userNumber: string): Promise<ControllerResponse> {
  const id = `2${userNumber.padStart(2, '0')}`;
  const res = await fetch(`${GARAGE_CONTROLLER_URL}?ID=${id}&REQ_TYPE=REQ_USER`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return parseControllerResponse(await res.text());
}

/**
 * Garage page – integrates with the elevator-based garage controller.
 * States: empty → loading (queued + polling) → waiting (elevator ready) → parked.
 */
export default function Garage() {
  const { _t } = useLang();
  const { id = '01' } = useParams<{ id: string }>();
  const [phase, setPhase] = useState<GaragePhase>('empty');
  const [queuePosition, setQueuePosition] = useState(0);
  const [garageError, setGarageError] = useState<GarageError | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const garageName = `${_t('Garage')} ${id}`;
  const isParked = phase === 'parked';
  const vehiclesBefore = Math.max(0, queuePosition - 1);

  /** Stop any active polling interval. */
  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  /** React to a controller response and update state accordingly. */
  const handleResponse = useCallback(
    (resp: ControllerResponse) => {
      switch (resp.reqAccepted) {
        case 'IN_PROCESS':
          setQueuePosition(resp.queuePosition);
          setPhase('loading');
          break;
        case 'YES':
          stopPolling();
          setPhase('waiting');
          break;
        case 'QUEUE_FULL':
          stopPolling();
          setGarageError('QUEUE_FULL');
          setPhase('error');
          break;
        case 'NOT':
        default:
          stopPolling();
          setGarageError('NOT');
          setPhase('error');
          break;
      }
    },
    [stopPolling]
  );

  /** Send request, handle response, and start polling when in queue. */
  const handlePark = useCallback(async () => {
    setPhase('loading');
    setQueuePosition(0);
    setGarageError(null);
    try {
      const resp = await callController(id);
      handleResponse(resp);
      if (resp.reqAccepted === 'IN_PROCESS') {
        pollRef.current = setInterval(async () => {
          try {
            handleResponse(await callController(id));
          } catch {
            stopPolling();
            setGarageError('NETWORK');
            setPhase('error');
          }
        }, POLL_INTERVAL_MS);
      }
    } catch {
      setGarageError('NETWORK');
      setPhase('error');
    }
  }, [id, handleResponse, stopPolling]);

  /** Cancel parking and return to empty state. */
  const handleCancel = useCallback(() => {
    stopPolling();
    setPhase('empty');
  }, [stopPolling]);

  /** User confirmed car is loaded into elevator. */
  const handleContinue = useCallback(() => setPhase('parked'), []);

  /** Call vehicle back (OUT mode – TODO: send OUT request when provides the command). */
  const handleCallVehicle = useCallback(() => setPhase('empty'), []);

  // Clean up polling on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  return (
    <Box bg="gray.0" mih="100vh" p={16}>
      <Paper p={20} radius={16} shadow="xs" bg="white">
        {/* ── Top: image + info ── */}
        <Flex gap={16} align="center" mb={24}>
          <img
            src={isParked ? '/img/garage.png' : '/img/garage-empty.png'}
            alt={_t('Garage')}
            style={{ width: 88, height: 88, objectFit: 'contain', flexShrink: 0 }}
          />
          <Stack gap={8}>
            <Text fw={700} fz={18} lh={1.3} c="gray.9">
              {garageName}
            </Text>
            <Badge
              variant="dot"
              color={isParked ? 'green' : 'gray'}
              radius={'xl'}
              style={{ textTransform: 'none', fontWeight: 500 }}
            >
              {isParked ? _t('Car is parked') : _t('Garage is empty')}
            </Badge>
          </Stack>
        </Flex>

        {/* ── State: empty ── */}
        {phase === 'empty' && (
          <Button fullWidth variant="primary" size="md" leftSection={<IconCar size={22} />} onClick={handlePark}>
            {_t('Park car')}
          </Button>
        )}

        {/* ── State: loading (queued, polling) ── */}
        {phase === 'loading' && (
          <Flex align="center" justify="space-between" gap={12} px={4}>
            <Flex align="flex-start" gap={10}>
              <IconInfoHexagon size={20} color="#8691A1" style={{ flexShrink: 0, marginTop: 2 }} />
              <Stack gap={2}>
                <Text fz={14} c="gray.7" fw={500}>
                  {_t('Please wait for the elevator.')}
                </Text>
                {vehiclesBefore > 0 && (
                  <Text fz={14} c="gray.6">
                    {sprintf(_t('There are %d vehicles before you.'), vehiclesBefore)}
                  </Text>
                )}
              </Stack>
            </Flex>
            <Loader size="sm" color="violet" style={{ flexShrink: 0 }} />
          </Flex>
        )}

        {/* ── State: waiting (elevator ready) ── */}
        {phase === 'waiting' && (
          <Stack gap={12}>
            <Flex gap={12}>
              <Button style={{ flex: 1 }} variant="secondary" size="md" onClick={handleCancel}>
                {_t('Cancel')}
              </Button>
              <Button style={{ flex: 1 }} variant="primary" size="md" onClick={handleContinue}>
                {_t('Continue')}
              </Button>
            </Flex>
            <Text fz={13} c="gray.6" ta="center" lh={1.6} style={{ whiteSpace: 'pre-line' }}>
              {_t('Put the car in and press the elevator button. Then tap continue.')}
            </Text>
          </Stack>
        )}

        {/* ── State: parked ── */}
        {phase === 'parked' && (
          <Button fullWidth variant="primary" size="md" leftSection={<IconCar size={22} />} onClick={handleCallVehicle}>
            {_t('Call vehicle')}
          </Button>
        )}

        {/* ── State: error ── */}
        {phase === 'error' && (
          <Stack gap={12} align="center">
            <Text fz={14} c="red.6" ta="center">
              {garageError === 'QUEUE_FULL'
                ? _t('The queue is full. Try again later.')
                : garageError === 'NETWORK'
                  ? _t('Elevator connection error.')
                  : _t('The request was not accepted.')}
            </Text>
            <Button variant="secondary" size="md" onClick={handleCancel}>
              {_t('Try again')}
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
